import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { api } from "@/api/index";
import { ChevronDownIcon } from "@/components/Icons/ChevronDownIcon";
import { cn } from "@/lib/helpers/cn";
import { ExitIcon } from "@/components/Icons/ExitIcon";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { getProviderProfile, isAuthorized, logOut } from "@/redux/actions/auth";
import { ICON_SIZES, ICON_TYPES } from "@/constants/icons";
import { SettingsIcon } from "@/components/Icons/SettingsIcon";
import { usePopUpVisibility } from "@/utils/hooks";
import { UserIcon } from "@/components/Icons/UserIcon";

import { MenuItem } from "./MenuItem";
import "./profile-menu-styles.scss";

const PRACTICE_COLORS = [
  "#E03260",
  "#4C8BF4",
  "#5B57DC",
  "#579ABE",
  "#01579B",
  "#8A74A1",
  "#2EB67C",
  "#006B3C",
  "#DC4C40",
  "#682861",
  "#EAB930",
  "#B72C3C",
  "#FF8C4C",
  "#66381E",
  "#5C0991",
  "#526079",
];

export function CommonMenu(props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [popupWasOpened, setPopupWasOpened] = useState(false);

  const {
    hidePopup: dismissMenu,
    isPopUpVisible,
    ref: popUpRef,
    togglePopup: toggleProfileMenu,
  } = usePopUpVisibility(false);

  const handleSettings = () => {
    dismissMenu();
    history.push(props.baseRoute + "/settings");
  };

  const handleProfile = () => {
    dismissMenu();
    history.push(props.baseRoute + "/profile");
  };

  const handleLogout = () => {
    dismissMenu();
    dispatch(logOut({ history }));
  };

  let isBHSProvider = false,
    isProvider;

  const practiceInfo = (practiceId) => {
    let practiceColor, practiceInitials, practiceName;

    if (practiceId != null && props.profile.practices_by_id != null) {
      practiceName = props.profile.practices_by_id[practiceId].name;
      practiceInitials = practiceName
        .split(" ")
        .map((word) => word.substring(0, 1).toUpperCase())
        .slice(0, 2)
        .join("");

      let practiceIndex = props.profile.practice_ids.indexOf(practiceId);

      practiceColor = PRACTICE_COLORS[practiceIndex % PRACTICE_COLORS.length];
    }

    return [practiceName, practiceInitials, practiceColor];
  };

  let name = "";
  let currentPracticeId, currentPracticeName;
  let acceptPatientsText = "";

  if (props.profile) {
    isProvider = Object.hasOwn(props.profile, "provider_type");
    name = isAuthorized("user:patient")
      ? props.profile.first_name + " " + props.profile.last_name
      : props.profile.name;
    currentPracticeId = isProvider ? props.profile.practice_id : null;

    if (currentPracticeId != null) {
      let [practiceName] = practiceInfo(currentPracticeId);

      currentPracticeName = practiceName;
    }

    isBHSProvider = isProvider ? props.profile.provider_type.startsWith("BEHAVIORAL") : false;
    acceptPatientsText = isBHSProvider
      ? props.profile.accept_new_patients
        ? "ACCEPTING PATIENTS"
        : "NOT ACCEPTING PATIENTS"
      : "";
  }

  const pickPractice = async (practiceId) => {
    if (practiceId === currentPracticeId) {
      return;
    }

    if (isProvider) {
      const data = { practice_id: practiceId };

      await api.provider.update_profile({ data });
      dispatch(getProviderProfile());
    }

    toggleProfileMenu();
    history.push("/app/home");
    history.go();
  };

  const profileDropdown = (
    <div style={{ display: popupWasOpened ? "block" : "none" }}>
      <div
        className={`Toolbar__ProfileMenu-dropdown d-flex flex-column align-items-center p-4 fs-14 mr-3 ${
          isPopUpVisible ? "menuOpen" : "menuClose"
        } ${!isPopUpVisible ? "disabled-dropdown" : ""}`}
      >
        {isBHSProvider && (
          <span className="fs-14 accepting-patients-text">
            <svg
              // CYPRESS TEST: Provider status indicator - DO NOT REMOVE
              className="accepting-patients-icon"
              height="20"
              width="20"
              {...getAutomationDataAttr("CommonMenuAcceptPatients")}
            >
              {/* CYPRESS TEST: Provider status icon - DO NOT REMOVE */}
              <circle
                cx="10"
                cy="10"
                fill={props.profile.accept_new_patients ? "#6AC060" : "red"}
                r="6"
              />
            </svg>
            {acceptPatientsText}
          </span>
        )}

        <div
          className="d-flex flex-column fs-22 mt-1 mb-4 text-center profile-name-container tw:font-bold"
          {...getAutomationDataAttr("CommonMenuNameContainer")}
        >
          {/* CYPRESS TEST: Provider name container - DO NOT REMOVE */}
          {name}
          {isProvider && (
            <span
              className="fs-14 provider-type tw:font-normal"
              {...getAutomationDataAttr("CommonMenuProviderType")}
            >
              {/* CYPRESS TEST: Provider type display - DO NOT REMOVE */}
              {props.profile.provider_type
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(" ")}
            </span>
          )}
        </div>

        {isProvider && (
          <div className="w-100 provider-list mb-3">
            <ul
              // CYPRESS TEST: Practice selection list - DO NOT REMOVE
              className="w-100 d-flex practice-list tw:justify-center"
              {...getAutomationDataAttr("CommonMenuPracticeList")}
            >
              {props.profile.practice_ids.map((practiceId, index) => {
                let [name] = practiceInfo(practiceId);
                const isSelectedPractice = props.profile.practice_id === practiceId;

                return (
                  <div key={practiceId}>
                    <li
                      className={cn(
                        "cursor tw:flex tw:items-center tw:justify-center",
                        isSelectedPractice ? "tw:font-bold" : "unselected-practice", // CYPRESS TEST: Unselected practice styling - DO NOT REMOVE
                      )}
                      onClick={() => pickPractice(practiceId)}
                      {...(!isSelectedPractice &&
                        getAutomationDataAttr("CommonMenuUnselectedPractice"))}
                    >
                      <span>{name}</span>
                    </li>

                    {index !== props.profile.practice_ids.length - 1 && (
                      <div className="d-flex align-items-center practice-separator tw:justify-center" />
                    )}
                  </div>
                );
              })}
            </ul>
          </div>
        )}

        <h6 className="col-12 border-bottom" />
        <div className="tw:mt-3 tw:flex tw:w-full tw:flex-col tw:gap-1" role="menu">
          <MenuItem
            automationDataAttr={getAutomationDataAttr("ProfileMenuProfile")}
            className="tw:flex tw:w-full tw:cursor-pointer tw:items-center"
            icon={UserIcon}
            onClick={handleProfile}
            text="Profile"
          />

          <MenuItem
            automationDataAttr={getAutomationDataAttr("ProfileMenuSettings")}
            className="tw:flex tw:w-full tw:cursor-pointer tw:items-center"
            icon={SettingsIcon}
            onClick={handleSettings}
            text="Settings"
          />

          <MenuItem
            className="tw:flex tw:w-full tw:cursor-pointer tw:items-center"
            icon={ExitIcon}
            onClick={handleLogout}
            text="Logout"
          />
        </div>
      </div>
    </div>
  );

  const nameRender = name ? (
    <span
      className="fs-16 txt-ellipsis NavUserNameSpan"
      style={{ whiteSpace: "nowrap" }}
      {...getAutomationDataAttr("CommonMenuUserName")}
    >
      {/* CYPRESS TEST: Used for provider/admin name display - DO NOT REMOVE */}
      {name}
    </span>
  ) : null;

  const currentPracticeNamerender = currentPracticeName ? (
    <span className="Toolbar__ProfileMenu-patients fs-10 txt-ellipsis">{currentPracticeName}</span>
  ) : null;

  const profileMenuItem = (
    <div
      className="Toolbar__ProfileMenu cursor d-flex justify-content-start align-items-center"
      onClick={toggleProfileMenu}
    >
      {(nameRender || currentPracticeNamerender) && (
        <div
          className={cn(
            "Toolbar__ProfileMenu-name-wpr",
            "d-lg-flex flex-column d-md-none d-sm-none d-none align-content-end",
            "tw:hover:text-text-teal-secondary!",
            currentPracticeNamerender && "with-practice",
          )}
          data-private="true"
          style={!isProvider ? { marginTop: 0 } : {}}
        >
          {nameRender}
          <div className="practice-name d-flex align-content-end">{currentPracticeNamerender}</div>
        </div>
      )}

      <div
        className="Toolbar__ProfileMenu-caret ml-2 d-lg-block d-md-none d-sm-none d-none"
        {...getAutomationDataAttr("ProfileMenu")}
      >
        <ChevronDownIcon className="Toolbar__ChevronDown" iconSize={ICON_SIZES.SMALL} />
      </div>

      <div
        className="Toolbar__ProfileMenu-name-wpr d-block d-lg-none d-flex flex-column align-items-center"
        style={{ fill: "#FFFFFF", marginTop: 0 }}
      >
        <UserIcon iconSize={ICON_SIZES.SMALL} iconType={ICON_TYPES.OUTLINE} />
        <span
          className="fs-16 txt-ellipsis ToolbarUserName"
          style={{ whiteSpace: "nowrap" }}
          {...getAutomationDataAttr("CommonMenuUserNameMobile")}
        >
          {/* CYPRESS TEST: Used in mobile view - DO NOT REMOVE */}
          {name}
        </span>
      </div>
    </div>
  );

  useEffect(() => {
    if (!popupWasOpened && isPopUpVisible) {
      setPopupWasOpened(true);
    }
  }, [isPopUpVisible]);

  return (
    <div className="tw:relative tw:flex tw:items-center" ref={popUpRef}>
      {props.profile ? profileMenuItem : <span className="Toolbar__ProfileMenu-placeholder" />}
      {profileDropdown}
    </div>
  );
}
